import { Button } from '@helvetia/components/Button'
import { QuestionWrapper } from '@helvetia/components/forms/form.styles'
import { Option } from '@helvetia/components/forms/NativeSelect/NativeSelect.types'
import { OptionSelectorField } from '@helvetia/components/forms/OptionSelector'
import { Col, Row } from '@helvetia/components/layout'
import LinkText from '@helvetia/components/LinkText'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { Field, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import { LifecycleView } from '../../_components/ConnectComponent'
import { DropDownSelect } from '../../_components/DropDownSelect'
import { RadioGroup } from '../../_components/RadioGroup'
import { AdobeEventManager } from '../../_utils/adobeAnalytics'
import { CalculatorStepper } from '../CaculatorStepper'
import { TypeOfGoodsPresenter } from './TypeOfGoods.presenter'
import { validateFieldForm } from './TypeOfGoods.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface TypeOfGoodsValues {
  transRemoval: number
  transTheft: number
  liability: string
  removalRadioValue: string
  theftRadioValue: string
}

interface Props {
  presenter: TypeOfGoodsPresenter
  onSubmit: (values: TypeOfGoodsValues) => void
}

interface State {
  initialValues?: TypeOfGoodsValues
  isEditing: boolean
  isLoading: boolean
}

export class TypeOfGoodsRaw
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: TypeOfGoodsPresenter
  private removalList: Option[]
  private theftList: Option[]
  private liabilityList: Option[]
  private YesNoList: Option[]

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.liabilityList = [
      {
        text: props.t('step4.liability.OR'),
        value: 'OR',
      },
      { text: props.t('step4.liability.AB'), value: 'AB' },
      { text: props.t('step4.liability.ASTAG'), value: 'ASTAG' },
    ]

    this.removalList = [
      { text: '10%', value: 10 },
      { text: '20%', value: 20 },
      { text: '30%', value: 30 },
      { text: '40%', value: 40 },
      { text: '50%', value: 50 },
      { text: '60%', value: 60 },
      { text: '70%', value: 70 },
      { text: '80%', value: 80 },
      { text: '90%', value: 90 },
      { text: '100%', value: 100 },
    ]

    this.theftList = this.removalList.slice()

    this.YesNoList = [
      {
        text: props.t('YesNoRadio.no'),
        value: 'false',
      },
      {
        text: props.t('YesNoRadio.yes'),
        value: 'true',
      },
    ]

    this.state = {
      initialValues: null,
      isEditing: false,
      isLoading: false,
    }
  }

  setInitialValues = (initialValues: TypeOfGoodsValues, isEditing: boolean) => {
    initialValues.liability = initialValues.liability
      ? initialValues.liability
      : this.liabilityList[0].value.toString()
    initialValues.transRemoval = initialValues.transRemoval
      ? initialValues.transRemoval
      : Number(this.removalList[0].value)
    initialValues.transTheft = initialValues.transTheft
      ? initialValues.transTheft
      : Number(this.theftList[0].value)
    this.setState({
      initialValues,
      isEditing,
    })
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  handleClickBack = () => this.presenter.onBackClick()

  handleClickContinuar = (values: TypeOfGoodsValues) =>
    this.presenter.onContinue(values)

  handleLinkClick = (values: TypeOfGoodsValues) => this.presenter.saveAndRedirect(values)


  render() {
    const { t } = this.props
    const { isEditing } = this.state

    if (!this.state.initialValues) return <Spinner size="xl" />

    return (
      <Row>
        <Col tablet={ 12 } desktop={ 4 } verticalGutter>
          <CalculatorStepper step={ 4 } />
        </Col>
        <Col tablet={ 12 } desktop={ 8 }>
          <Row>
            <Text as="p" color="black" size="l">
              { t('step4.title') }
            </Text>
          </Row>

          <Formik
            initialValues={ this.state.initialValues }
            onSubmit={ this.handleClickContinuar }
            validate={ values => validateFieldForm(values) }
            render={ ({ values, setFieldValue }) => (
              <Form>
                <Field
                  component={ RadioGroup }
                  name="removalRadioValue"
                  questionText={ t('step4.removal.question') }
                  radioList={ this.YesNoList }
                  defaultSelected={ values.removalRadioValue }
                  onChange={ e => {
                    setFieldValue('removalRadioValue', e.currentTarget.value)
                    if (e.currentTarget.value === 'false') {
                      setFieldValue('transRemoval', 0)
                    }
                  } }
                />
                { values.removalRadioValue === 'true' && (
                  <Row marginTop="xxs" marginBottom="s">
                    <Col>
                      <MatchMedia onlyMobile>
                        <Field
                          component={ DropDownSelect }
                          defaultSelected={ values.transRemoval }
                          name="transRemoval"
                          question={ t('step4.removal.frequency') }
                          options={ this.removalList }
                        />
                      </MatchMedia>
                      <MatchMedia tablet>
                        <Field
                          component={ OptionSelectorField }
                          name="transRemoval"
                          textLabel={ t('step4.removal.frequency') }
                          optionList={ this.removalList }
                        />
                      </MatchMedia>
                    </Col>
                  </Row>
                ) }
                <Row marginTop="xs">
                  <Col>
                    <QuestionWrapper as="div" color="black">
                      { t('step4.theft.question') }
                    </QuestionWrapper>
                    <ul
                      style={ {
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        justifySelf: 'center',
                        marginTop: '8px',
                        marginBottom: '10px',
                      } }
                    >
                      { [...Array(8)].map((x, i) => (
                        <li key={ i }>{ t(`step4.theft.list.item${i + 1}`) }</li>
                      )) }
                    </ul>
                    <Field
                      component={ RadioGroup }
                      name="theftRadioValue"
                      radioList={ this.YesNoList }
                      defaultSelected={ values.theftRadioValue }
                      onChange={ e => {
                        setFieldValue('theftRadioValue', e.currentTarget.value)
                        if (e.currentTarget.value === 'false') {
                          setFieldValue('transTheft', 0)
                        }
                      } }
                    />
                  </Col>
                </Row>
                { values.theftRadioValue === 'true' && (
                  <Row marginTop="xxs" marginBottom="s">
                    <Col>
                      <MatchMedia onlyMobile>
                        <Field
                          component={ DropDownSelect }
                          defaultSelected={ values.transTheft }
                          name="transTheft"
                          question={ t('step4.theft.frequency') }
                          options={ this.theftList }
                        />
                      </MatchMedia>
                      <MatchMedia tablet>
                        <Field
                          component={ OptionSelectorField }
                          name="transTheft"
                          textLabel={ t('step4.theft.frequency') }
                          optionList={ this.theftList }
                        />
                      </MatchMedia>
                    </Col>
                  </Row>
                ) }
                <Row marginTop="m" marginBottom="s">
                  <Col>
                    <MatchMedia upToLargeDesktop>
                      <Field
                        component={ DropDownSelect }
                        defaultSelected={ values.liability }
                        name="liability"
                        question={ t('step4.liability.question') }
                        options={ this.liabilityList }
                      />
                    </MatchMedia>
                    <MatchMedia largeDesktop>
                      <Field
                        component={ OptionSelectorField }
                        name="liability"
                        textLabel={ t('step4.liability.question') }
                        optionList={ this.liabilityList }
                      />
                    </MatchMedia>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Text as="div" color="black" size="s">
                      { t('step4.liability.note1') }
                    </Text>
                    <Text as="div" color="black" size="s">
                      { t('step4.liability.note2') }
                      <LinkText
                        onClick={ () => this.handleLinkClick(values) }
                        text={ t('step4.liability.cancelLink') }
                        color="primary"
                      />
                    </Text>
                  </Col>
                </Row>

                <Row verticalGutter marginTop="xs" marginBottom="m">
                  <Col verticalGutter mobile={ 12 } tablet={ 3 } desktop={ 3 }>
                    <Button primary type="submit">
                      { isEditing ? t('buttons.save') : t('buttons.continue') }
                    </Button>
                  </Col>
                  <Col verticalGutter mobile={ 12 } tablet={ 3 } desktop={ 3 }>
                    <Button grey onClick={ this.handleClickBack }>
                      { isEditing ? t('buttons.cancel') : t('buttons.back') }
                    </Button>
                  </Col>
                </Row>
              </Form>
            ) }
          />
        </Col>
      </Row>
    )
  }
}

export const TypeOfGoodsView = withNamespaces()(withRouter(TypeOfGoodsRaw))
